import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, CheckCircle, UserCircle, Target, Building2 } from 'lucide-react';
import { fetchRecentPlatformActivity } from '@/lib/api';

const iconMap = {
  UserPlus,
  CheckCircle,
  UserCircle,
  Target,
  Building2,
};

export function RecentActivity() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const colorMap = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
  };

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const result = await fetchRecentPlatformActivity(18);
      setActivities(result);
      setLoading(false);
    }
    loadData();
  }, []);

  const groupedActivities = activities.reduce((groups, activity) => {
    const key = activity.user || 'User';
    const existing = groups.get(key) || {
      user: key,
      latestTime: activity.time,
      activities: [] as typeof activities,
      highScoreCount: 0,
    };
    existing.activities.push(activity);
    if (activity.type === 'achievement') existing.highScoreCount += 1;
    groups.set(key, existing);
    return groups;
  }, new Map<string, { user: string; latestTime: string; activities: typeof activities; highScoreCount: number }>());

  const reps = Array.from(groupedActivities.values()).slice(0, 6);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Recent Activity</h2>
        </div>
        <div className="h-[200px] flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Recent Activity</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
          <div className="text-muted-foreground">No recent platform activity</div>
          <div className="text-sm text-muted-foreground">Activity will appear as users interact with the platform</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-foreground">Recent Rep Activity</h2>
        <button 
          className="text-primary text-sm font-medium hover:underline"
          onClick={() => navigate('/admin/users')}
        >
          View All
        </button>
      </div>
      <div className="space-y-4">
        {reps.map((rep) => {
          const latest = rep.activities[0];
          const Icon = iconMap[latest.icon as keyof typeof iconMap];
          return (
            <div key={rep.user} className="flex items-start gap-4 pb-4 border-b border-border last:border-0">
              <div className={`w-10 h-10 rounded-lg ${colorMap[latest.color]} flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-foreground truncate">{rep.user}</div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{rep.latestTime}</div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {rep.activities.length} recent {rep.activities.length === 1 ? 'session' : 'sessions'}
                  {rep.highScoreCount > 0 ? ` · ${rep.highScoreCount} high score${rep.highScoreCount === 1 ? '' : 's'}` : ''}
                </div>
                <div className="mt-2 space-y-1">
                  {rep.activities.slice(0, 3).map((activity: any, index: number) => (
                    <div key={`${rep.user}-${index}`} className="text-xs text-muted-foreground truncate">
                      {activity.description}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
